// The staff message-template library, cached for the composer's template picker
// (GET /api/admin/message-templates — the shared clinic library plus the caller's
// own personal templates).
//
// Deliberately lazy and event-free: unlike the counts stores there is no interval
// and no WS subscription, because templates only change when someone saves one in
// Settings — and the editor calls invalidateMessageTemplates() when they do. The
// cost of that simplicity is that a template another user edits in a different
// session is not picked up until this session reloads or opens Settings, which is
// the right trade for config that changes a few times a month.

import { fetchMessageTemplates } from '@/lib/api/message-templates';
import { getAuthToken, getAuthUser, subscribeAuthStateChange } from '@/lib/api/token-store';

let templates = null; // normalized rows, or null before the first successful load
let canEditShared = false;
let started = false;
let inFlight = null;
const listeners = new Set();

function emit() {
  for (const listener of listeners) {
    try {
      listener(templates);
    } catch {
      /* ignore listener failures */
    }
  }
}

// Identity the cached library belongs to. It contains the caller's PERSONAL
// templates, so a profile switch must drop it immediately rather than wait for the
// new user's first fetch to land — a failed fetch would otherwise leave user A's
// private wording in user B's picker.
let templatesOwnerUserId = '';

function currentUserId() {
  return String(getAuthUser()?.id || '').trim();
}

function dropIfOwnerChanged(userId) {
  if (templates === null || templatesOwnerUserId === userId) return;
  templates = null;
  canEditShared = false;
  templatesOwnerUserId = '';
  emit();
}

/** Fetch the library, replacing the cache. Concurrent calls share one request. */
export async function refreshMessageTemplates() {
  if (!getAuthToken()) {
    if (templates !== null) {
      templates = null;
      canEditShared = false;
      templatesOwnerUserId = '';
      emit();
    }
    return null;
  }
  if (inFlight) return inFlight;

  const requestUserId = currentUserId();
  dropIfOwnerChanged(requestUserId);

  inFlight = (async () => {
    try {
      const next = await fetchMessageTemplates();
      // Identity guard: a profile switch mid-flight must not land user A's personal
      // templates in user B's picker.
      if (currentUserId() !== requestUserId) return templates;
      templates = next.items;
      canEditShared = next.canEditShared;
      templatesOwnerUserId = requestUserId;
      emit();
    } catch {
      /* keep the previous (same-user) library; the next open retries */
    } finally {
      inFlight = null;
    }
    return templates;
  })();

  return inFlight;
}

function ensureStarted() {
  if (started || typeof window === 'undefined') return;
  started = true;
  subscribeAuthStateChange(() => {
    dropIfOwnerChanged(currentUserId());
  });
}

/** The cached library, or null until the first successful load. */
export function getMessageTemplates() {
  ensureStarted();
  dropIfOwnerChanged(currentUserId());
  return templates;
}

/** Whether this user may write the shared library (settings:manage), per the server. */
export function getCanEditSharedTemplates() {
  ensureStarted();
  dropIfOwnerChanged(currentUserId());
  return canEditShared;
}

/** Load once if the cache is cold. Safe to call on every picker open. */
export function ensureMessageTemplatesLoaded() {
  ensureStarted();
  dropIfOwnerChanged(currentUserId());
  if (templates !== null || inFlight) return inFlight ?? Promise.resolve(templates);
  return refreshMessageTemplates();
}

/**
 * Drop the cache and reload. Called by the Settings editor after every save/delete so
 * the composer picker can never serve wording that was just changed.
 */
export function invalidateMessageTemplates() {
  ensureStarted();
  templates = null;
  templatesOwnerUserId = '';
  emit();
  return refreshMessageTemplates();
}

export function subscribeMessageTemplates(listener) {
  if (typeof listener !== 'function') return () => {};
  ensureStarted();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
